import { isNonRetryableClientError, reportAsyncError } from "@istari-frontend/shared";
import { MutationCache, type Query, QueryCache, QueryClient } from "@tanstack/react-query";

export interface AppMutationMeta {
  /** @deprecated Use `triggerGlobalInvalidation: false` at the mutation call site. */
  skipGlobalInvalidation?: boolean;
  /** Set to `false` to opt out. Undefined falls through so callers default to global invalidation. */
  triggerGlobalInvalidation?: boolean;
}

// A type alias, not an interface: query-core only adopts a registered meta that extends
// Record<string, unknown>, which an interface does not, and would silently fall back to it.
export type AppQueryMeta = {
  /**
   * Set on queries read imperatively with no observer (`fetchQuery`) whose caller classifies and
   * reports failures itself. It only suppresses the unobserved `query-cache` report, which would
   * duplicate that.
   */
  reportsOwnErrors?: boolean;
  immutableContent?: boolean;
};

export function metadataOnlyQuery(query: Query): boolean {
  return query.meta?.immutableContent !== true;
}

export const immutableContentQueryOptions = {
  retry: false,
  gcTime: 0,
  staleTime: Infinity,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  placeholderData: undefined,
  meta: { immutableContent: true },
} as const;

declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: AppMutationMeta;
    queryMeta: AppQueryMeta;
  }
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Non-retryable 4xx (bad ids, invalid params, permission failures) can't
      // recover on retry. Transient 4xx (408, 425, 429) and 5xx are still retried.
      retry: (failureCount, error) => {
        if (isNonRetryableClientError(error)) return false;
        return failureCount < 3;
      },
    },
  },
  // Catches background failures the UI never surfaces: prefetch storms, stale
  // background refetches, useQuery results no observer is reading. Pair with
  // observer-level handling for mutations users actually wait on.
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.getObserversCount() > 0) return;
      if (query.meta?.reportsOwnErrors) return;
      // React Query's default queryHash serializes the key, which can contain content-token salts.
      reportAsyncError("query-cache", error);
    },
  }),
  mutationCache: new MutationCache({
    onSuccess: (_data, _variables, _context, mutation) => {
      // === false: undefined falls through so callers default to global invalidation
      if (mutation.meta?.skipGlobalInvalidation || mutation.meta?.triggerGlobalInvalidation === false) return;
      logInvalidation();
      queryClient.invalidateQueries({ predicate: metadataOnlyQuery });
    },
  }),
});

function logInvalidation(): void {
  if (import.meta.env.DEV && localStorage.getItem("debug:invalidation") === "1") {
    const observers = queryClient
      .getQueryCache()
      .getAll()
      .filter((q) => q.getObserversCount() > 0);
    console.info("[mutation→invalidate]", {
      observerCount: observers.length,
    });
  }
}
